"use client";
import React, { useState, useMemo } from "react";
import {
  Search, ChevronDown, ChevronUp, IndianRupee, CalendarClock,
  FileText, Link as LinkIcon, Users, ClipboardList, Download,
  ExternalLink, ShieldAlert, CheckCircle2, Clock, TrendingUp,
  Wallet, PieChart, Clock3, X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useAdminProjectInsights } from "../../context/AdminProjectInsightsContext";

/**
 * AdminProjectInsights
 * ---------------------------------------------------------------------------
 * Admin-only, read-only insights view for Projects.
 * UI ONLY — no API calls. Replace MOCK_PROJECTS below with real data from
 * your ProjectContext / API once ready. Every field the admin needs is
 * modeled here: client, deadline, full budget vs. amount paid so far,
 * requirements gathered from the client, documents shared, and websites
 * / references shared.
 * ---------------------------------------------------------------------------
 */

// ── Status → color map (kept consistent with the Projects table) ───────────
const STATUS_STYLES = {
  Pending:       { badge: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-400" },
  Started:       { badge: "bg-sky-100 text-sky-700",       dot: "bg-sky-400" },
  Planning:      { badge: "bg-indigo-100 text-indigo-700", dot: "bg-indigo-400" },
  Designing:     { badge: "bg-purple-100 text-purple-700", dot: "bg-purple-400" },
  "In Progress": { badge: "bg-blue-100 text-blue-700",     dot: "bg-blue-400" },
  Reviewing:     { badge: "bg-orange-100 text-orange-700", dot: "bg-orange-400" },
  Finalizing:    { badge: "bg-teal-100 text-teal-700",     dot: "bg-teal-400" },
  "On Hold":     { badge: "bg-gray-200 text-gray-700",     dot: "bg-gray-400" },
  Completed:     { badge: "bg-green-100 text-green-700",   dot: "bg-green-400" },
  Cancelled:     { badge: "bg-red-100 text-red-700",       dot: "bg-red-400" },
};

const currency = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN");

const daysLeft = (deadline) => {
  if (!deadline) return null;

  const diff = Math.ceil(
    (new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24)
  );

  return isNaN(diff) ? null : diff;
};

export default function AdminProjectInsights() {

  const {
    projects,
    summary,
    loading
  } = useAdminProjectInsights();

  const { user, canDo } = useAuth?.() || {};

  // Admin gate — adjust this check to however admin role is represented
  // in your auth context (e.g. user.role === "admin").
  const isAdmin =
    user?.role?.toLowerCase?.() === "admin" ||
    (typeof canDo === "function" && canDo("Projects", "Insights"));

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [expanded, setExpanded] = useState(() => new Set([projects[0]?.id]));

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const filtered = useMemo(() => {
  return projects.filter((p) => {
    const q = search.toLowerCase().trim();

    const projectName = (p.project_name || "").toLowerCase();
    const clientName = (p.client_name || "").toLowerCase();
    const project_type = (p.project_type || "").toLowerCase();
    const status = p.status || "";

    const matches =
      projectName.includes(q) ||
      clientName.includes(q) ||
      project_type.includes(q);

    const statusOk =
      statusFilter === "All" ||
      status === statusFilter;

    return matches && statusOk;
  });
}, [projects, search, statusFilter]);

  const totals = useMemo(() => {
    const totalBudget = projects.reduce((s, p) => s + Number(p.budget || 0), 0);
    const totalPaid = projects.reduce((s, p) => s + Number(p.paid_amount || 0), 0);
    return {
      count: projects.length,
      totalBudget,
      totalPaid,
      totalDue: totalBudget - totalPaid,
    };
  }, [projects]);

  if (!isAdmin) {
    return (
      <div className="bg-sky-50 min-h-[60vh] flex items-center justify-center px-6 py-10">
        <div className="bg-white rounded-2xl shadow-md p-8 text-center max-w-sm">
          <ShieldAlert className="mx-auto text-red-400 mb-3" size={36} />
          <h3 className="text-lg font-semibold text-gray-800 mb-1">Admins only</h3>
          <p className="text-sm text-gray-500">
            Project insights are only visible to admin accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-sky-50">
      <div className="px-6 py-6 max-sm:px-4">
        <div className="mb-5">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Project Insights</h2>
          <p className="text-sm text-gray-500 mt-1">
            Full visibility for admins — deadlines, payments, requirements, and everything clients have shared.
          </p>
        </div>

        {/* ── Summary strip ─────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard icon={ClipboardList} color="indigo" label="Total Projects" value={summary.total_projects} />
          <SummaryCard icon={Wallet} color="blue" label="Total Budget" value={currency(summary.totalBudget)} />
          <SummaryCard icon={CheckCircle2} color="green" label="Completed" value={(summary.total_completed)} />
          <SummaryCard icon={Clock3} color="red" label="Pending" value={(summary.total_pending)} />
        </div>

        {/* ── Toolbar ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 mb-6 bg-white px-5 py-4 rounded-xl shadow-sm">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by project, client or project type"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg outline-none text-gray-700 text-sm focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 outline-none bg-white focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
          >
            {["All", ...Object.keys(STATUS_STYLES)].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          {(search || statusFilter !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatusFilter("All");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>

        {/* ── Project cards ─────────────────────────────────────────────── */}
        <div className="space-y-4">
          {filtered.map((p) => {
            const isOpen = expanded.has(p.id);
            const pct = p.budget ? Math.min(100, Math.round((p.paid_amount / p.budget) * 100)) : 0;
            const dLeft = daysLeft(p.deadline);
            const statusStyle = STATUS_STYLES[p.status] || STATUS_STYLES.Pending;

            return (
              <div key={p.id} className="bg-white rounded-2xl shadow-md overflow-hidden">
                {/* Row header */}
                <button
                  onClick={() => toggle(p.id)}
                  className="w-full flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-[220px]">
                    <span className={`w-2.5 h-2.5 rounded-full ${statusStyle.dot}`} />
                    <div>
                      <h3 className="font-semibold text-gray-800">{p.project_name}</h3>
                      <p className="text-xs text-gray-500">{p.client_name} · {p.project_type}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
  <CalendarClock size={14} />

  {p.deadline || "No deadline"}

  {dLeft !== null && (
    <span
      className={`ml-1 font-medium ${
        dLeft < 0
          ? "text-red-500"
          : dLeft <= 7
          ? "text-orange-500"
          : "text-gray-400"
      }`}
    >
      {dLeft < 0
        ? `${Math.abs(dLeft)}d overdue`
        : `${dLeft}d left`}
    </span>
  )}
</div>

                    <div className="w-36">
                      <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                        <span>{currency(p.paid_amount)} paid</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: "linear-gradient(90deg, #2563eb, #d12465)",
                          }}
                        />
                      </div>
                    </div>

                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyle.badge}`}>
                      {p.status}
                    </span>

                    {isOpen ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </button>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t px-5 py-5 grid md:grid-cols-2 gap-6 bg-gray-50/50">
                    {/* Payment breakdown */}
                    <DetailBlock icon={IndianRupee} title="Payment">
                      <Row label="Total budget" value={currency(p.budget)} />
                      <Row label="Paid till now" value={currency(p.paid_amount)} valueClass="text-green-600" />
                      <Row label="Balance due" value={currency(p.budget - p.paid_amount)} valueClass="text-red-500" />
                      <Row label="Last payment" value={p.last_payment_date || "—"} />
                    </DetailBlock>

                    {/* Timeline */}
                    <DetailBlock icon={CalendarClock} title="Timeline">
                      <Row label="Start date" value={p.start_date} />
                      <Row label="Deadline" value={p.deadline} />
                      <Row
                        label="Status"
                        value={
                          <span className="flex items-center gap-1">
                            {p.status === "Completed" ? (
                              <CheckCircle2 size={14} className="text-green-500" />
                            ) : (
                              <Clock size={14} className="text-orange-400" />
                            )}
                            {p.status}
                          </span>
                        }
                      />
                    </DetailBlock>

                    {/* Description + requirements */}
                    <DetailBlock icon={ClipboardList} title="Requirements from client" full>
                      <p className="text-sm text-gray-600 mb-3">{p.description}</p>
                      {p.requirements?.length ? (
                        <ul className="space-y-1.5">
                          {p.requirements.map((r, i) => (
                            <li key={i} className="text-sm text-gray-700 flex gap-2">
                              <span className="text-indigo-400 mt-0.5">•</span> {r}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400">No requirements noted yet.</p>
                      )}
                    </DetailBlock>

                    {/* Documents */}
                    <DetailBlock icon={FileText} title="Documents shared by client">
                      {p.documents?.length ? (
                        <ul className="space-y-2">
                          {p.documents.map((d, i) => (
                            <li key={i} className="flex items-center justify-between text-sm bg-white rounded-lg border px-3 py-2">
                              <span className="flex items-center gap-2 text-gray-700 truncate">
                                <FileText size={14} className="text-indigo-400 shrink-0" /> {d.name}
                              </span>
                              <span className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
                                {d.size}
                                <Download size={14} className="text-gray-400 hover:text-indigo-500 cursor-pointer" />
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400">No documents shared.</p>
                      )}
                    </DetailBlock>

                    {/* Websites / references + team */}
                    <DetailBlock icon={LinkIcon} title="Websites & references shared">
                      {p.websites?.length ? (
                        <ul className="space-y-2 mb-4">
                          {p.websites.map((w, i) => (
                            <li key={i} className="flex items-center justify-between text-sm bg-white rounded-lg border px-3 py-2">
                              <span className="text-gray-700 truncate">{w.label}</span>
                              <a
                                href={w.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-xs text-indigo-500 hover:underline shrink-0"
                              >
                                Visit <ExternalLink size={12} />
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 mb-4">No links shared.</p>
                      )}

                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <Users size={14} /> Team assigned
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {p.team?.map((t, i) => (
                          <span key={i} className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded-full">
                            {t}
                          </span>
                        ))}
                      </div>
                    </DetailBlock>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="bg-white rounded-2xl shadow-md p-8 text-center text-gray-400 text-sm">
              No projects match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Small presentational helpers ────────────────────────────────────────────
function SummaryCard({ icon: Icon, color, label, value }) {
  return (
    <div className={`bg-white rounded-2xl shadow-md p-4 flex items-center gap-3 border border-${color}-100`}>
      <div className={`w-10 h-10 rounded-xl bg-${color}-50 text-${color}-500 flex items-center justify-center shrink-0`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <p className="text-lg font-semibold text-gray-800 truncate">{value}</p>
      </div>
    </div>
  );
}

function DetailBlock({ icon: Icon, title, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
        <Icon size={16} className="text-indigo-500" /> {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, valueClass = "text-gray-800" }) {
  return (
    <div className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className={`font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}